import { Flex, Heading } from "@chakra-ui/react"
import type { BRREGUnit } from "@esgt/types"
import { RatingProfileSelector, useRatingProfiles } from "components/RatingProfileSelector"
import { RatingProfileInfoTooltipIcon } from "components/Tooltips"
import { config } from "lib/helpers/config"
import { useEffect } from "react"
import { type FieldValues, useForm } from "react-hook-form"

export type NewRatingProfileSettings = {
  ratingProfileId: number
}

interface RatingProfileFormProps {
  values: Partial<NewRatingProfileSettings>
  setValues: (v: Partial<NewRatingProfileSettings>) => void
  selectedOrganization?: BRREGUnit
}

export const RatingProfileForm = ({ values, setValues, selectedOrganization }: RatingProfileFormProps) => {
  const { profiles, defaultProfile, isFetching, hasOnlyOneProfile } = useRatingProfiles(
    selectedOrganization,
    config.SELFSERVICE_PROFILE_METHOD,
  )

  const { register, watch } = useForm({
    defaultValues: {
      ratingProfileId: defaultProfile,
    } as FieldValues,
  })

  useEffect(() => {
    if (defaultProfile) {
      setValues({ ...values, ratingProfileId: Number(defaultProfile) })
    }
  }, [defaultProfile, setValues])

  useEffect(() => {
    const subscription = watch((value) => {
      setValues({
        ratingProfileId: Number(value.ratingProfileId) || Number(defaultProfile),
      })
    })
    return () => subscription.unsubscribe()
  }, [defaultProfile, setValues, watch])

  // Hide the selector entirely when there's only one profile (e.g. VSME baseline)
  if (hasOnlyOneProfile) {
    return null
  }

  return (
    <Flex gap={7} p={8} direction={"column"} as="form">
      <Flex justifyContent="space-between">
        <Heading as="h2" size="md">
          Velg målingsprofil <RatingProfileInfoTooltipIcon />
        </Heading>
      </Flex>

      <RatingProfileSelector
        register={register}
        profiles={profiles}
        defaultProfile={defaultProfile}
        isFetching={isFetching}
        currentValue={values.ratingProfileId}
      />
    </Flex>
  )
}
